import { type FC, useState } from "react";

import { CourseGroupingDropdown } from "./CourseGroupingDropdown";

import { COURSE_ID_QUERY_PARAM } from "components/constants";
import { Trans, t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { TextField } from "mds/components/TextField";
import { storeApi } from "store/index";
import { toastSuccessMessage } from "utils/alerts";
import { useQueryParamsManager } from "utils/routing";

type AddCourseModalProps = {
  eventName?: string;
  eventProperties?: Record<string, unknown>;
  onClose: () => void;
  defaultGroupingId?: string | null;
};

const AddCourseModalComponent: FC<AddCourseModalProps> = ({
  eventName,
  eventProperties,
  onClose,
  defaultGroupingId,
}) => {
  const [name, setName] = useState("");
  const [selectedGroupingId, setSelectedGroupingId] = useState<string | null>(
    defaultGroupingId || null,
  );
  const { setQueryParams } = useQueryParamsManager();

  const handleSubmit = () => {
    if (!name.trim()) {
      return;
    }

    storeApi.courses
      .create({
        grouping_id: selectedGroupingId,
        code: name.trim(),
      })
      .then((newItem) => {
        toastSuccessMessage(t("home.toasts.course_added"));
        setQueryParams({ [COURSE_ID_QUERY_PARAM]: newItem.id });
        onClose();
      });
  };

  return (
    <div className="mt-8 flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <label className="flex flex-col gap-1">
          <div>{t("nav_list.new_title_course")}</div>
          <TextField
            eventName={eventName}
            eventProperties={eventProperties}
            placeholder={t("home.placeholder.course_name")}
            value={name}
            isEditing
            onChange={(e) => setName(String(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && name.trim()) {
                handleSubmit();
              }
            }}
          />
        </label>
      </div>

      <div className="flex flex-col gap-2">
        <div className="font-medium">
          <Trans i18nKey="home.courses.add_to_grouping_label">
            Add to Folder
            <span className="font-normal text-black-tint-55">(Optional)</span>
          </Trans>
        </div>

        <CourseGroupingDropdown
          selectedGroupingId={selectedGroupingId}
          onSelectionChange={setSelectedGroupingId}
        />
      </div>

      <div className="flex w-full justify-end gap-2">
        <Button kind="white" size="s" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </Button>

        <Button
          disabled={!name.trim()}
          kind="blue"
          size="s"
          title={t("common.add.course")}
          onClick={handleSubmit}
        >
          {t("common.add.course")}
        </Button>
      </div>
    </div>
  );
};

export const AddCourseModal = AddCourseModalComponent;
